import axios, { type AxiosInstance, type InternalAxiosRequestConfig } from 'axios'
import { toast } from 'sonner'
import { API_URL, STORAGE_KEYS } from '@/utils/app-constants'
import type { ApiResponse } from '@/types/api'

let isRefreshing = false
let refreshSubscribers: Array<(token: string) => void> = []
let lastNetworkErrorToast = 0

function subscribeTokenRefresh(callback: (token: string) => void) {
  refreshSubscribers.push(callback)
}

function onRefreshed(token: string) {
  refreshSubscribers.forEach((cb) => cb(token))
  refreshSubscribers = []
}

const api: AxiosInstance = axios.create({
  baseURL: API_URL,
  timeout: 30_000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor — attach Bearer token
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN)
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => Promise.reject(error),
)

// Response interceptor — handle 401, 500 errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config

    // Handle 401 — try token refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      const refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN)

      if (!refreshToken) {
        // No refresh token → logout
        handleLogout()
        return Promise.reject(error)
      }

      if (isRefreshing) {
        // Queue this request until token is refreshed
        return new Promise((resolve) => {
          subscribeTokenRefresh((token: string) => {
            originalRequest.headers.Authorization = `Bearer ${token}`
            resolve(api(originalRequest))
          })
        })
      }

      originalRequest._retry = true
      isRefreshing = true

      try {
        const response = await axios.post(
          `${API_URL}/api/v1/auth/refresh`,
          { refresh_token: refreshToken },
          { headers: { 'Content-Type': 'application/json' } },
        )

        const payload = (response.data as ApiResponse<{
          access_token: string
          refresh_token: string
        }>).data
        const { access_token, refresh_token: newRefreshToken } = payload
        localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, access_token)
        if (newRefreshToken) {
          localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, newRefreshToken)
        }

        onRefreshed(access_token)
        isRefreshing = false

        originalRequest.headers.Authorization = `Bearer ${access_token}`
        return api(originalRequest)
      } catch (_refreshError) {
        isRefreshing = false
        refreshSubscribers = []
        handleLogout()
        return Promise.reject(error)
      }
    }

    // Silently drop rate-limit errors — the request will succeed once the window resets
    if (error.response?.status === 429) {
      return Promise.reject(error)
    }

    // Handle 500 errors — show toast
    if (error.response?.status >= 500) {
      toast.error('Erro interno do servidor. Tente novamente mais tarde.')
    }

    // Deduplicate network/CORS errors — show at most once per 15 s
    if (!error.response) {
      const now = Date.now()
      if (now - lastNetworkErrorToast > 15_000) {
        lastNetworkErrorToast = now
        toast.error('Sem conexão com o servidor. Verifique sua internet.')
      }
    }

    return Promise.reject(error)
  },
)

function handleLogout() {
  localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN)
  localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN)
  localStorage.removeItem(STORAGE_KEYS.USER)
  // Redirect to login — use window.location to avoid circular imports
  if (window.location.pathname !== '/login') {
    window.location.href = '/login'
  }
}

export default api
